import { useMemo } from 'react';
import { getPostBody, getPostBySlug } from '@/content/blog/loadPosts';
import { Box, Chip, Divider, Typography, useTheme } from '@mui/material';
import { format } from 'date-fns';
import { ArrowLeft } from 'lucide-react';
import { Link, useParams } from 'react-router-dom';

import { BlogMarkdown } from '@/components/Marketing/BlogMarkdown';
import { pillLabelSx } from '@/components/Marketing/blogStyles';
import { MarketingPageShell } from '@/components/Marketing/MarketingPageShell';
import { useJsonLd } from '@/hooks/useJsonLd';
import { usePageMeta } from '@/hooks/usePageMeta';

const SITE_URL = 'https://unbeatableloans.com';

const BackToBlogLink = () => {
  const theme = useTheme();
  return (
    <Box
      component={Link}
      to="/blog"
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 1,
        px: 2,
        py: 1,
        borderRadius: 2,
        border: `1px solid ${theme.palette.greyShades.border2}`,
        color: 'text.secondary',
        fontSize: '0.875rem',
        fontWeight: 600,
        textDecoration: 'none',
        transition: 'border-color 0.2s ease, color 0.2s ease',
        '&:hover': {
          borderColor: theme.palette.mainColors.blue,
          color: theme.palette.mainColors.blue,
        },
      }}
    >
      <ArrowLeft size={16} />
      Back to blog
    </Box>
  );
};

export default function BlogPostPage() {
  const { slug } = useParams<{ slug: string }>();
  const post = slug ? getPostBySlug(slug) : undefined;
  const body = slug ? getPostBody(slug) : undefined;

  usePageMeta(post?.title ?? 'Blog', post?.description, {
    image: post?.image,
  });

  const jsonLd = useMemo(() => {
    if (!post) return undefined;
    return {
      '@context': 'https://schema.org',
      '@type': 'BlogPosting',
      headline: post.title,
      description: post.description,
      datePublished: post.publishedAt,
      dateModified: post.publishedAt,
      image: post.image ? [post.image] : undefined,
      mainEntityOfPage: `${SITE_URL}/blog/${post.slug}`,
      author: { '@type': 'Person', name: post.author.name },
      publisher: {
        '@type': 'Organization',
        name: 'Unbeatable Loans',
        logo: { '@type': 'ImageObject', url: `${SITE_URL}/logo128.png` },
      },
    };
  }, [post]);
  useJsonLd(jsonLd);

  if (!slug || !post || !body) {
    return (
      <MarketingPageShell title="Article not found" showHero>
        <Typography color="text.secondary" sx={{ mb: 2 }}>
          We could not find that article.
        </Typography>
        <BackToBlogLink />
      </MarketingPageShell>
    );
  }

  return (
    <MarketingPageShell showHero={false}>
      <Box sx={{ maxWidth: 760, mx: 'auto' }}>
        <Box sx={{ mb: 4 }}>
          <BackToBlogLink />
        </Box>

        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mb: 2 }}>
          <Chip
            label={post.category}
            size="small"
            color="primary"
            sx={pillLabelSx}
          />
        </Box>

        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 700,
            fontSize: { xs: '2rem', md: '2.75rem' },
            lineHeight: 1.15,
            mb: 2,
          }}
        >
          {post.title}
        </Typography>

        <Typography
          variant="h6"
          color="text.secondary"
          sx={{ fontWeight: 400, mb: 2 }}
        >
          {post.description}
        </Typography>

        <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
          By {post.author.name} ·{' '}
          {format(new Date(post.publishedAt), 'MMMM d, yyyy')} ·{' '}
          {post.readTimeMinutes} min read
        </Typography>

        <BlogMarkdown content={body} />

        <Divider sx={{ my: 4 }} />

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 4 }}>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
              {post.author.name}
            </Typography>
            <Typography variant="body2" color="text.secondary">
              {post.author.role}
            </Typography>
          </Box>
        </Box>

        <BackToBlogLink />
      </Box>
    </MarketingPageShell>
  );
}
